<template>
	<div class="input-wrapper">
		<template v-if="labelText">
			<div v-if="$slots.tooltip" class="row justify-content-between flex-nowrap has-tooltip">
				<label :id="uniqueLabel"
					   class="label form-label col-auto"
					   v-html="labelText" />
				<slot name="tooltip" />
			</div>
			<label v-else
				   :id="uniqueLabel"
				   class="label form-label"
				   v-html="labelText" />
		</template>

		<div class="input-i-wrapper">
			<input v-model.trim="fieldInput"
				   :aria-labelledby="uniqueLabel"
				   class="input form-control"
				   :class="{
				   'input--valid' : !v$.fieldInput.$invalid && fieldInput.length> 0 && !forceErrorText,
			'input--error': v$.fieldInput.$error,
			'input--valid__currency': inputType === 'currency' && !v$.fieldInput.$invalid && fieldInput.length > 0,
			'input-format__currency': inputType === 'currency',
			'input-format__postcode': inputType === 'postcode',
			'input-format__houseno': inputType === 'houseno'
			}"
			:maxLength="textType.maxLength"
			:type="textType.inputType"
			:inputmode="this.isNumericType ? 'numeric' : undefined"
			:placeholder="textType.placeholder"
			:autocomplete="textType.autocomplete"
			:character-limit-value="this.characterLimitValue"
			:character-limit-validation-text="this.characterLimitValidationText"
			@blur="handleBlur"
			@focus="handleFocus"
			@input="handleInput"
			@keydown="handleKeyDown"
			>
			<slot name="append" />
		</div>

		<p v-if="v$.fieldInput.$error || forceErrorText"
		   class="error-text">
			{{ errorTextComputed }}
		</p>
		<p v-else-if="furtherInformationText"
		   class="further-information-text">
			{{ furtherInformationText }}
		</p>
		<p v-else-if="supportText"
		   class="support-text">
			{{ supportText }}
		</p>
	</div>
</template>
<script>
	import { postcodeValidator, mobileValidator, phoneValidator, NationalInsuranceNumberValidator, ExceedingMaxCharLength } from '../vuelidate/index.js';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, email, minLength, maxLength } from '@vuelidate/validators';
import { intervalToDuration, parse, isValid } from 'date-fns';


export default {
	props: {
		inputType: {
			type: String,
			default: 'text',
		},
		labelText: {
			type: String,
			default: '',
		},
		furtherInformationText: {
			type: String,
			default: '',
		},
		errorText: {
			type: String,
			default: '',
		},
		supportText: {
			type: String,
			default: '',
		},
		required: {
			type: Boolean,
			default: false,
		},
		maxLength: {
			type: Number,
			default: null,
		},
		prefillInput: {
			type: String,
			default: '',
			required: false,
		},
		minimumValue: {
			type: Number,
			default: 0,
			required: false,
		},
		maximumValue: {
			type: Number,
			default: 999999999,
			required: false,
		},
		manualInputDirtier: {
			type: Boolean,
			default: false,
		},
		forceErrorText: {
			type: Boolean,
			default: false,
		},
		autocomplete: {
			type: String,
			default: '',
		},
		preventEmptyValidation: {
			type: Boolean,
			default: false,
		},
		characterLimitValidationText: {
			type: String,
			default: '',
		},
		characterLimitValue: {
			type: Number,
			default: 999999999,
			required: false,
		},
	},
	emits: ['field-input', 'input-error'],
	data() {
		return {
			v$: useVuelidate(),
			fieldInput: '',
		};
	},
	computed: {
		textType() {
			switch (this.inputType) {
				case 'postcode':
					return {
						inputType: 'text',
						maxLength: 9,
						autocomplete: 'postal-code',
					};
				case 'email':
					return {
						inputType: 'email',
						autocomplete: 'email',
					};
				case 'currency':
					return {
						placeholder: '',
						inputType: 'text',
					};
				case 'bankAccount':
					return {
						placeholder: '',
						inputType: 'text',
						maxLength: 8,
					};
				case 'phoneNumber':
					return {
						inputType: 'tel'
					};
				case 'mobileNumber':
					return {
						inputType: 'tel',
						maxLength: 11,
						autocomplete: this.autocomplete || null
					};
				case 'dob':
					return {
						placeholder: 'DD/MM/YYYY',
						inputType: 'text',
						maxLength: 10,
						autocomplete: 'bday',
					};
				case 'NiN':
					return {
						inputType: 'text',
					};
				default:
					return {
						inputType: 'text',
						maxLength: this.maxLength,
						autocomplete: this.autocomplete || null
					};
			}
		},

		isNumericType() {
			return this.inputType === 'currency'
				|| this.inputType === 'age'
				|| this.inputType === 'years'
				|| this.inputType === 'bankAccount';
		},

		// DOB is the only field that has separate error messages
		errorTextComputed() {
			if (this.inputType === 'dob' && !this.v$.fieldInput.isValidDate.$response) {
				return 'Please enter a valid date of birth in the format 01 08 1970';
			}
			else if (this.inputType === 'dob' && !this.v$.fieldInput.isValidAge.$response) {
				return 'The date of birth you entered would make you ineligible for our With Profits ISA. You must be between 18 and 74 to apply.';
			}
			else if (this.characterLimitValidationText && this.fieldInput.length >= this.characterLimitValue) {
				return this.characterLimitValidationText;
			}
			else {
				return this.errorText;
			};
		},
		uniqueLabel() {
			return this.labelText + '-label';
		},
	},
	watch: {
		prefillInput(newValue, oldValue) {
			this.fieldInput = newValue;
		},
		manualInputDirtier(newValue, oldValue) {
			if (newValue) {
				this.v$.fieldInput.$dirty = true;
			}
		},
	},
	methods: {
		formatDOB(event) {
			let cursorPosition = event.target.selectionStart;

			if (
				['Backspace', 'Delete', 'Tab'].includes(event.key)
				|| [37, 39, 35].includes(event.keyCode)
			) { }
			else if (event.key >= 0 && event.key <= 9) {
				if (event.target.value.length == 2) {
					const formattedDate = event.target.value.replace(/[^0-9]/g, '').replace(/(\d{2})/, '$1/');
					this.fieldInput = formattedDate;
				}
				else if (event.target.value.length >= 5) {
					const formattedDate = event.target.value.replace(/[^0-9]/g, '').replace(/(\d{2})(\d{2})/, '$1/$2/');
					this.fieldInput = formattedDate;
				}
				else if (event.target.value.length == 10) {
					const formattedDate = event.target.value.replace(/[^0-9]/g, '').replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3');
					this.fieldInput = formattedDate;
				}
			}
			// allow / only after 2 or 5 characters
			else if ([2,5].includes(event.target.value.length) && event.key === '/') {}
			else {
				event.preventDefault();
			}

			event.target.setSelectionRange(cursorPosition, cursorPosition);
		},
		formatNationalInsuranceNumber() {
			let result = '';
			this.fieldInput = this.fieldInput.replace(/\s/g, '').toUpperCase();
			for (let i = 0; i < this.fieldInput.length; i++) {
				result += this.fieldInput[i];
				if ((i + 1) % 2 === 0 && i < this.fieldInput.length - 1) {
					result += ' ';
				}
			}
			this.fieldInput = result;
		},
		handleBlur() {
			if (this.preventEmptyValidation && this.fieldInput.length === 0) {
				return;
			}
			else {
				this.v$.fieldInput.$dirty = true;
			}
			if (this.inputType === 'NiN' && this.fieldInput !== '') {
				this.formatNationalInsuranceNumber();
			}
			if (this.inputType === 'currency' && this.fieldInput !== '') {
				this.fieldInput = parseFloat(this.fieldInput).toLocaleString('en', { useGrouping: true });
			}
			if (this.v$.fieldInput.$error) {
				this.$emit('input-error', true)
			}
			else if (!this.v$.fieldInput.$error) {
				this.$emit('input-error', false)
			}
		},
		handleFocus() {
			if (['currency', 'NiN'].includes(this.inputType)) {
				this.fieldInput = this.fieldInput.replace(/[,\s]+/g, '');
			}
		},
		handleKeyDown(event) {
			if (this.inputType === 'dob') {
				this.formatDOB(event);
			}
			if (['currency', 'bankAccount'].includes(this.inputType) && event.key.length === 1 && /\D/.test(event.key)) {
				event.preventDefault();
			}
		},
		handleInput() {
			this.$emit('field-input', {
				value: this.fieldInput,
				isValid: (!this.v$.fieldInput.$invalid && this.fieldInput.length > 0),
			});
		},
		calculateAge() {
			const todaysDate = new Date();

			const birthDate = parse(this.fieldInput, 'dd/MM/yyyy', new Date());
			let age = intervalToDuration({
				start: birthDate,
				end: todaysDate,
			}).years;
			if (age > 74 || age < 18) {
				return false;
			}
			return true;
		},

	},
	mounted() {
		if (this.prefillInput) {
			this.fieldInput = this.prefillInput;
		}
	},
	validations() {
		switch (this.inputType) {
			case 'postcode':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						postcodeValidator,
					},
				};
			case 'email':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						email,
					},
				};
			case 'phoneNumber':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						phoneValidator,
						minLength: minLength(11),
					},
				};
			case 'mobileNumber':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						mobileValidator,
					},
				};
			case 'currency':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						isBetweenMinAndMax: (value) => {
							return value.replace(/[,\s]+/g, '') >= this.minimumValue && value.replace(/[,\s]+/g, '') <= this.maximumValue;
						},
					},
				};
			case 'bankAccount':
				return {
					fieldInput: {
						required: required,
						accountNumber: (value) => [6, 7, 8].includes(value.length)
					},
				};
			case 'dob':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						minLength: minLength(10),
						isValidDate: (value) => {
							if (value.length === 10) {
								const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
								return isValid(parsedDate);
							}
						},
						isValidAge: () => {
							return this.calculateAge();
						}
					},
				};
			case 'NiN':
				return {
					fieldInput: {
						required: requiredIf(this.required),
						NationalInsuranceNumberValidator,
					},
				};
			default:
				return {
					fieldInput: {
						required: requiredIf(this.required),
						maxCharacters: this.characterLimitValue ? ExceedingMaxCharLength(this.characterLimitValue): null
					},
				};
		}
	},
};

/**
 * @typedef {{
 * value: string;
 * isValid: boolean;
 * }} TextInputData
 */

</script>

<style scoped lang="scss">
	$error: #d72d41;
	$default-text-color: #41474f;

	.further-information-text {
		font-size: 6px;
		background-image: url('/assets/images/furtherInformationQuestionMark.svg');
		background-position: left center;
		background-repeat: no-repeat;
		background-size: 6px;
		padding-left: 8px;
		margin-top: 2px;
		color: $default-text-color;
	}

	.support-text {
		font-size: 0.9375rem;
		margin-top: 8px;
		color: #41474f;
	}

	.input {
		// TODO: Can we delete these?

		&:not(.input--valid, .input--error):focus {
			border-color: #40474f;
			outline: none;
			border-width: 1px;
		}
	}


	// Not currently working to remove the arrow buttons from the text input field when the format is number
	.input-format__age::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Firefox */
	.input-format__age[type=number] {
		-moz-appearance: textfield;
	}
</style>
